import { useState, useEffect } from 'react';
import { Input, Button, FormGroup, Label, Form, Progress } from 'reactstrap';
import i18n from '@/utils/i18n';
import ContainerListView from './ContainerListView';
import ContainerTypeSelector from '../../components/ContainerType/ContainerTypeSelector';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import { Dialog } from 'primereact/dialog';
import { Communicator } from '../../tools/Communication';
import { Spinner } from 'reactstrap';
import { LayoutStoreInstance } from '../../LayoutStore';
import { PageContainer, PageHeader } from '../../components/Layout';
import { FaStar, FaUpload, FaDownload } from 'react-icons/fa';
import { IconButton } from '../../components/Button/Button';
import { download } from '@/utils/download';
import Checkbox from '../../components/Checkbox';
import { ContentSection } from '../../components/Layout/ContentSection';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export interface ContainerListPageProps {
  storeId: string | undefined;
}

const ContainerListPage = (props: ContainerListPageProps) => {
  useDocumentTitle('PageTitles:containers');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContainerCreationDialog, setShowContainerCreationDialog] =
    useState(false);
  const [containerCreationAmount, setContainerCreationAmount] = useState<
    number | null
  >(null);
  const [containerCreationTypeId, setContainerCreationTypeId] =
    useState<string>('1');
  const [containerCreationPrefix, setContainerCreationPrefix] =
    useState<string>('');
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [showContainerUpdateDialog, setShowContainerUpdateDialog] =
    useState(false);
  const [containerUpdateCsvFile, setContainerUpdateCsvFile] =
    useState<File | null>(null);
  const [containerUpdateProcessed, setContainerUpdateProcessed] = useState(0);
  const [containerUpdateTotal, setContainerUpdateTotal] = useState(0);
  const [
    containerUpdateProgressIntervalId,
    setContainerUpdateProgressIntervalId,
  ] = useState(0);
  const [showEpcGenerationModal, setShowEpcGenerationModal] = useState(false);
  const [useBomCheck, setUseBomCheck] = useState(true);
  // Add these new state variables:
  const [showBumerangImportDialog, setShowBumerangImportDialog] =
    useState(false);
  const [bumerangCsvFile, setBumerangCsvFile] = useState<File | null>(null);
  const [bumerangContainerTypeId, setBumerangContainerTypeId] =
    useState<number>(1);
  const [showAlignDialog, setShowAlignDialog] = useState(false);
  const [alignContainerTypeId, setAlignContainerTypeId] = useState<number>(1);

  useEffect(() => {
    Communicator.Get('container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: ContainerType[] }) => {
        setContainerTypes(data.containerTypes);
      })
      .catch(() => {
        // If loading fails the selector simply stays empty; container
        // creation is blocked until a valid type is chosen.
      });
  }, []);

  const uuid = () => {
    var buf = new Uint32Array(4);
    window.crypto.getRandomValues(buf);
    var idx = -1;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        idx++;
        var r = (buf[idx >> 3] >> ((idx % 8) * 4)) & 15;
        var v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  const renderContainerCreationDialog = () => {
    if (!showContainerCreationDialog) {
      return null;
    }

    const hide = () => !isSubmitting && setShowContainerCreationDialog(false);

    const execute = async () => {
      setIsSubmitting(true);
      try {
        const query = new URLSearchParams({
          amount: String(containerCreationAmount),
          containerTypeId: containerCreationTypeId,
          namePrefix: containerCreationPrefix,
        }).toString();
        const result = await Communicator.PostData(
          `/Container/GenerateContainers?${query}`,
          {},
        );
        if (result.ok) {
          let blob = await result.blob();
          let a = window.document.createElement('a');
          a.href = window.URL.createObjectURL(blob);
          a.download = `ContainerIds_${new Date()
            .toISOString()
            .replace(':', '.')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } catch (result) {
        const errorResponse = await (result as Response).json();
        if (errorResponse.errors && errorResponse.errors.length > 0) {
          alert(
            i18n.t('containerSearch:errorPrefix', {
              errors: errorResponse.errors.join(','),
            }),
          );
        } else {
          alert(i18n.t('containerSearch:unknownErrorPleaseRetry'));
        }
      }
      setIsSubmitting(false);
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={
            !containerCreationAmount || !containerCreationTypeId || isSubmitting
          }
          color="danger"
        >
          {i18n.t('containerSearch:createContainersAndDownload')}
        </Button>
        <Button onClick={hide} disabled={isSubmitting} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('Common:createNewContainers')}
        visible={showContainerCreationDialog}
        onHide={hide}
        closable={!isSubmitting}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        {isSubmitting ? (
          <div className="text-center">
            <Spinner />
            <div>{i18n.t('containerSearch:processingPleaseWait')}</div>
          </div>
        ) : (
          <Form>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:amountOfContainersToCreate')}{' '}
              </Label>
              <Input
                id="containerCreationAmount"
                name="containerCreationAmount"
                type="number"
                value={containerCreationAmount || ''}
                onChange={(e: any) =>
                  setContainerCreationAmount(e.target.value as number)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:selectContainerType')}
              </Label>
              <ContainerTypeSelector
                data={containerTypes}
                value={containerCreationTypeId}
                onChange={(value) =>
                  setContainerCreationTypeId(
                    typeof value === 'string' ? value : '',
                  )
                }
                searchable
                clearable
                required
              />
            </FormGroup>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:containerNamePrefix')}
              </Label>
              <Input
                id="containerCreationPrefix"
                name="containerCreationPrefix"
                type="text"
                maxLength={30}
                placeholder={i18n.t(
                  'containerSearch:containerNamePrefixPlaceholder',
                )}
                value={containerCreationPrefix}
                onChange={(e: any) =>
                  setContainerCreationPrefix(e.target.value as string)
                }
              />
              <small className="d-block text-muted mt-1">
                {i18n.t('containerSearch:containerNamePrefixHelp')}
              </small>
              <small className="d-block mt-1">
                {i18n.t('containerSearch:exampleContainerName')}{' '}
                <code>{containerCreationPrefix}A1B2C3</code>
              </small>
            </FormGroup>
          </Form>
        )}
      </Dialog>
    );
  };

  const renderContainerUpdateDialog = () => {
    if (!showContainerUpdateDialog) {
      return null;
    }

    const hide = () => !isSubmitting && setShowContainerUpdateDialog(false);

    const execute = async () => {
      const callbackId = uuid();

      if (!useBomCheck) {
        if (
          !window.confirm(i18n.t('containerSearch:utf8EncodingConfirmation'))
        ) {
          return;
        }
      }
      setIsSubmitting(true);

      if (!containerUpdateCsvFile) {
        return alert(i18n.t('containerSearch:pleaseSelectFileToUpload'));
      }

      const postPromise = Communicator.PostFileUpload(
        `/Container/UpdateContainersFromCsv?callbackId=${callbackId}&useBomCheck=${useBomCheck}`,
        containerUpdateCsvFile,
      );

      const intervalId = setInterval(async (_) => {
        const progressResult = await Communicator.Get(
          `/Container/QueryUpdateContainerProcessingStatus`,
          { callbackId },
        );
        const progress = await progressResult.json();
        if (progress.processed && progress.total) {
          setContainerUpdateProcessed(progress.processed);
          setContainerUpdateTotal(progress.total);
        }
      }, 5000);
      setContainerUpdateProgressIntervalId(Number(intervalId));

      postPromise
        .then(async (postResult) => {
          if (postResult.ok) {
            LayoutStoreInstance.setSuccess(
              i18n.t('containerSearch:containersUpdatedSuccessfully'),
            );
          } else {
            const errorResponse = await postResult.json();
            if (errorResponse.errors && errorResponse.errors.length > 0) {
              alert(
                i18n.t('containerSearch:errorPrefix', {
                  errors: errorResponse.errors.join(','),
                }),
              );
            } else {
              alert(i18n.t('containerSearch:unknownErrorPleaseRetry'));
            }
          }
        })
        .catch(async (postResult) => {
          const errorResponse = await postResult.json();
          if (errorResponse.errors && errorResponse.errors.length > 0) {
            alert(
              i18n.t('containerSearch:errorPrefix', {
                errors: errorResponse.errors.join(','),
              }),
            );
          } else {
            alert(i18n.t('containerSearch:unknownErrorPleaseRetry'));
          }
        })
        .finally(() => {
          if (containerUpdateProgressIntervalId) {
            clearInterval(containerUpdateProgressIntervalId);
            setContainerUpdateProgressIntervalId(0);
          }
          setIsSubmitting(false);
          hide();
        });
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={!containerUpdateCsvFile || isSubmitting}
          color="danger"
        >
          {i18n.t('containerSearch:changeContainersAccordingToCsv')}
        </Button>
        <Button onClick={hide} disabled={isSubmitting} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:uploadContainerCsvFile')}
        visible={showContainerUpdateDialog}
        onHide={hide}
        closable={!isSubmitting}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        {isSubmitting ? (
          <div className="text-center">
            <Progress
              animated
              value={containerUpdateProcessed}
              max={containerUpdateTotal}
            />
            <div>
              {i18n.t('containerSearch:processingWithProgress', {
                processed: containerUpdateProcessed,
                total: containerUpdateTotal,
              })}
              <br />{' '}
              <strong>
                {i18n.t('containerSearch:thisCanTakeUpToTenMinutes')}
              </strong>
            </div>
          </div>
        ) : (
          <Form>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:containerCsvFile')}{' '}
              </Label>
              <Input
                id="containerCreationAmount"
                name="containerCreationAmount"
                type="file"
                onChange={(e: any) =>
                  setContainerUpdateCsvFile(
                    e.target.files && e.target.files.length > 0
                      ? e.target.files[0]
                      : null,
                  )
                }
              />
            </FormGroup>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:useBomCheck')}{' '}
              </Label>
              <Checkbox
                checked={useBomCheck || false}
                onClick={() => setUseBomCheck(!useBomCheck)}
              />
            </FormGroup>
          </Form>
        )}
      </Dialog>
    );
  };

  const renderContainerEpcGenerationDialog = () => {
    console.log(showEpcGenerationModal);
    if (!showEpcGenerationModal) {
      return null;
    }

    const hide = () => setShowEpcGenerationModal(false);
    const execute = async () => {
      if (!containerUpdateCsvFile) {
        return alert(i18n.t('containerSearch:pleaseSelectFileToUpload'));
      }

      try {
        const res = await Communicator.PostFileUpload(
          `/Container/GenerateEpcsFromCsv`,
          containerUpdateCsvFile,
        );
        LayoutStoreInstance.setSuccess(
          i18n.t('containerSearch:epcsGeneratedSuccessfully'),
        );
        const blob = await res.blob();
        download(blob, 'EPCs.csv');
      } catch (e) {
        LayoutStoreInstance.setError(
          i18n.t('containerSearch:errorWhileGeneratingEpcs'),
        );
        console.log(e);
      }
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={!containerUpdateCsvFile}
          color="danger"
        >
          {i18n.t('containerSearch:generateEpcs')}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:uploadContainerCsvFile')}
        visible={showEpcGenerationModal}
        onHide={hide}
        closable={true}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        {
          <Form>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('containerSearch:containerCsvFile')}{' '}
              </Label>
              <Input
                id="csv-file"
                name="csv-file"
                type="file"
                onChange={(e: any) => {
                  if (
                    window.confirm(
                      i18n.t('containerSearch:epcGenerationConfirmation'),
                    )
                  ) {
                    setContainerUpdateCsvFile(
                      e.target.files && e.target.files.length > 0
                        ? e.target.files[0]
                        : null,
                    );
                  }
                }}
              />
            </FormGroup>
          </Form>
        }
      </Dialog>
    );
  };

  const renderBumerangImportDialog = () => {
    if (!showBumerangImportDialog) {
      return null;
    }

    const hide = () => !isSubmitting && setShowBumerangImportDialog(false);

    const execute = async () => {
      if (!bumerangCsvFile) {
        return alert('Please select a Bumerang CSV file to upload');
      }

      setIsSubmitting(true);
      try {
        const res = await Communicator.PostFileUpload(
          `/Container/ImportBumerangContainersFromCsv?containerTypeId=${bumerangContainerTypeId}`,
          bumerangCsvFile,
        );

        const result = await res.json();
        LayoutStoreInstance.setSuccess(
          `Successfully imported ${result.ContainersCreated} Bumerang containers`,
        );
      } catch (e) {
        LayoutStoreInstance.setError(
          'Error while importing Bumerang containers: ' + e,
        );
        console.error(e);
      }
      setIsSubmitting(false);
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={!bumerangCsvFile || isSubmitting}
          color="primary"
        >
          {isSubmitting ? <Spinner size="sm" /> : 'Import Bumerang Containers'}
        </Button>
        <Button onClick={hide} disabled={isSubmitting} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header="Import Bumerang Containers"
        visible={showBumerangImportDialog}
        onHide={hide}
        closable={!isSubmitting}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">Container Type ID:</Label>
            <Input
              type="number"
              value={bumerangContainerTypeId}
              onChange={(e) =>
                setBumerangContainerTypeId(parseInt(e.target.value))
              }
              placeholder="Enter Container Type ID"
            />
          </FormGroup>
          <FormGroup>
            <Label className="me-2">Bumerang CSV File:</Label>
            <Input
              type="file"
              accept=".csv"
              onChange={(e: any) =>
                setBumerangCsvFile(
                  e.target.files && e.target.files.length > 0
                    ? e.target.files[0]
                    : null,
                )
              }
            />
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const renderAlignContainerIdsDialog = () => {
    if (!showAlignDialog) {
      return null;
    }

    const hide = () => !isSubmitting && setShowAlignDialog(false);

    const execute = async () => {
      if (!alignContainerTypeId) {
        return alert('Please enter a valid Container Type ID');
      }

      setIsSubmitting(true);
      try {
        const res = await Communicator.PostData(
          `/Container/AlignContainerIdsWithExternalGuid2?containerTypeId=${alignContainerTypeId}`,
          {},
        );

        const result = await res.json();
        LayoutStoreInstance.setSuccess(
          `Alignment complete: ${result.Updated} containers updated, ${result.SkippedNonGuid} skipped (non-GUID), ${result.SkippedAlreadySame} skipped (already aligned), ${result.SkippedExistingId} skipped (ID exists), ${result.SkippedWithDependencies} skipped (has dependencies)`,
        );
      } catch (e) {
        LayoutStoreInstance.setError(
          'Error while aligning container IDs: ' + e,
        );
        console.error(e);
      }
      setIsSubmitting(false);
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={!alignContainerTypeId || isSubmitting}
          color="primary"
        >
          {isSubmitting ? <Spinner size="sm" /> : 'Align Container IDs'}
        </Button>
        <Button onClick={hide} disabled={isSubmitting} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header="Align Container IDs with External GUID"
        visible={showAlignDialog}
        onHide={hide}
        closable={!isSubmitting}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">Container Type ID:</Label>
            <Input
              type="number"
              value={alignContainerTypeId}
              onChange={(e) =>
                setAlignContainerTypeId(parseInt(e.target.value))
              }
              placeholder="Enter Container Type ID"
            />
          </FormGroup>
          <div className="alert alert-info">
            <strong>Note:</strong> This will update container IDs to match their
            External IDs when:
            <ul>
              <li>External ID is a valid GUID</li>
              <li>The GUID is not already used as another container's ID</li>
              <li>The container has no dependent records (locations, etc.)</li>
            </ul>
          </div>
        </Form>
      </Dialog>
    );
  };

  const handleDownloadStoreTransitionReport = async () => {
    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';

    fileInput.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      setIsSubmitting(true);
      try {
        // Create FormData to upload the file
        const formData = new FormData();
        formData.append('csvFile', file);

        const result = await fetch('/Container/DownloadStoreTransitionReport', {
          method: 'POST',
          body: formData,
          credentials: 'include', // Include cookies for authentication
        });

        if (result.ok) {
          const blob = await result.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `store_transition_report_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          const errorText = await result.text();
          LayoutStoreInstance.setError(
            i18n.t('Containers:errorDownloadingReport') ||
              'Error downloading report',
          );
          console.error('Error downloading report:', errorText);
        }
      } catch (error) {
        LayoutStoreInstance.setError(
          i18n.t('Containers:errorDownloadingReport') ||
            'Error downloading report',
        );
        console.error('Error downloading report:', error);
      } finally {
        setIsSubmitting(false);
        // Clean up
        document.body.removeChild(fileInput);
      }
    };

    // Trigger the file picker
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  return (
    <PageContainer>
      <ContentSection
        title={i18n.t('Navigation:containers')}
        collapsable={false}
      >
        <PageHeader title={i18n.t('Navigation:containers')}>
          <IconButton
            className="mb-1 me-1"
            icon={<FaStar />}
            label={i18n.t('Containers:createNewContainers')}
            onClick={(_) => setShowContainerCreationDialog(true)}
          />
          <IconButton
            className="mb-1 me-1"
            icon={<FaUpload />}
            label={i18n.t('Containers:uploadContainerCsv')}
            onClick={(_) => setShowContainerUpdateDialog(true)}
          />
          <IconButton
            className="mb-1 me-1"
            icon={<FaUpload />}
            label={i18n.t('Containers:generateRfidEpcs')}
            onClick={(_) => {
              setShowEpcGenerationModal(true);
              console.log('ASD');
            }}
          />
          <IconButton
            className="mb-1 me-1"
            icon={<FaUpload />}
            label={i18n.t('Containers:importBumerangContainers')}
            onClick={(_) => setShowBumerangImportDialog(true)}
          />
          <IconButton
            className="mb-1 me-1"
            icon={<FaDownload />}
            label={i18n.t('Containers:downloadStoreTransitionReport')}
            onClick={handleDownloadStoreTransitionReport}
            isLoading={isSubmitting}
          />
          <IconButton
            className="mb-1 me-1"
            icon={<FaStar />}
            label={i18n.t('Containers:alignContainerIds')}
            onClick={(_) => setShowAlignDialog(true)}
          />
        </PageHeader>
        <ContainerListView />
        {renderContainerCreationDialog()}
        {renderContainerUpdateDialog()}
        {renderContainerEpcGenerationDialog()}
        {renderBumerangImportDialog()}
        {renderAlignContainerIdsDialog()}
      </ContentSection>
    </PageContainer>
  );
};

export default ContainerListPage;
